import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CameraIcon,
  ComputerDesktopIcon,
  PlayIcon,
  SignalIcon,
  MicrophoneIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../../store'
import { startSession, submitAssessment, getSessionStatus } from '../../store/slices/testSlice'
import { startTimer } from '../../store/slices/timerSlice'
import { useWebSocket } from '../../services/websocket'
import toast from 'react-hot-toast'
import { compressImage, capturePhotoFromVideo } from '../../utils/imageCompression'
import { config } from '../../config/env'

interface SystemCheck {
  camera: boolean | 'testing'
  microphone: boolean | 'testing'
  browser: boolean | 'testing'
  internet: boolean | 'testing'
}

interface NetworkSpeed {
  download: number | null
  upload: number | null
  latency: number | null
}

const TestSystemCheckPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { currentSession } = useSelector((state: RootState) => state.test)
  const { user } = useSelector((state: RootState) => state.auth)
  
  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    dispatch(getSessionStatus(sessionId)).unwrap().then(session => {
      if (!cancelled && ['submitted', 'completed', 'expired', 'cancelled'].includes(session?.status)) {
        navigate(`/test/${sessionId}/completed`, { replace: true })
      }
    }).catch(() => {
      if (!cancelled) navigate('/dashboard', { replace: true })
    })
    return () => { cancelled = true }
  }, [sessionId, dispatch, navigate])

  // Assessment sequence navigation prevention
  const [showSequenceWarning, setShowSequenceWarning] = useState(false)
  
  useEffect(() => {
    const assessmentSequenceStarted = sessionStorage.getItem('assessmentSequenceStarted') === 'true'
    const currentDriveAssessmentSequence = sessionStorage.getItem('currentDriveAssessmentSequence')
    
    if (!assessmentSequenceStarted || !currentDriveAssessmentSequence) {
      return
    }

    const handlePopState = (event: PopStateEvent) => {
      if (location.pathname.includes('/test/')) {
        event.preventDefault()
        setShowSequenceWarning(true)
        window.history.pushState(null, '', location.pathname)
      }
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'Navigation Back from test is not allowed, complete all Assessment in one go. Sure want to leave the test?'
      return event.returnValue
    }

    window.history.pushState(null, '', location.pathname)
    window.addEventListener('popstate', handlePopState)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [location.pathname])
  
  const [systemCheck, setSystemCheck] = useState<SystemCheck>({
    camera: 'testing',
    microphone: 'testing',
    browser: 'testing',
    internet: 'testing'
  })
  const [agreed, setAgreed] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [currentStep, setCurrentStep] = useState<'camera' | 'microphone' | 'network' | 'complete'>('camera')
  const [audioLevel, setAudioLevel] = useState(0)
  const [networkSpeed, setNetworkSpeed] = useState<NetworkSpeed>({
    download: null,
    upload: null,
    latency: null
  })
  const [networkTestProgress, setNetworkTestProgress] = useState<{
    phase: 'latency' | 'download' | 'upload' | 'complete' | null
    progress: number
  }>({ phase: null, progress: 0 })
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const testRunRef = useRef<string | null>(null) // Track which test has been run
  const [cameraErrorMessage, setCameraErrorMessage] = useState<string | null>(null)
  
  // Photo capture state
  const [photograph, setPhotograph] = useState<Blob | null>(null)
  const [photographPreview, setPhotographPreview] = useState<string | null>(null)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [photoCaptured, setPhotoCaptured] = useState(false)
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null)
  const [isCheckingPhoto, setIsCheckingPhoto] = useState(true)
  const imageRetryCountRef = useRef<number>(0) // Track retry attempts to prevent infinite loop
  
  const { connect } = useWebSocket()

  // Check if photo already exists on mount
  useEffect(() => {
    const checkExistingPhoto = async () => {
      if (!sessionId) return
      
      try {
        setIsCheckingPhoto(true)
        const response = await fetch(`/api/v1/test-sessions/${sessionId}/photograph`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          if (data.photograph_exists && data.photograph_url) {
            // CRITICAL: Fix spaces in URL immediately (database might have wrong format)
            // Replace spaces with underscores in the path
            let photoUrl = data.photograph_url.replace(/ /g, '_').replace(/%20/g, '_')
            
            // console.log('URL received from API:', {
            //   original: data.photograph_url,
            //   afterSpaceFix: photoUrl
            // })
            
            // Normalize URL - ensure it's an absolute URL
            
            // If it's already an absolute URL (http/https), use it as is
            if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
              // If it contains localhost, convert to current domain
              if (photoUrl.includes('localhost')) {
                try {
                  const url = new URL(photoUrl)
                  // Use current window origin instead of localhost
                  photoUrl = `${window.location.origin}${url.pathname}`
                } catch (e) {
                  // Fallback: extract path and use current origin
                  const match = photoUrl.match(/\/uploads\/.*$/)
                  if (match) {
                    photoUrl = `${window.location.origin}${match[0]}`
                  }
                }
              }
            } else {
              // If it's a relative URL, make it absolute
              // Ensure it starts with /
              if (!photoUrl.startsWith('/')) {
                photoUrl = `/${photoUrl}`
              }
              
              // IMPORTANT: Convert relative URL to absolute URL
              // Use window.location.origin to make it absolute
              const absoluteUrl = `${window.location.origin}${photoUrl}`
              
              // console.log('Converting relative URL to absolute:', {
              //   original: data.photograph_url,
              //   relative: photoUrl,
              //   absolute: absoluteUrl,
              //   origin: window.location.origin,
              //   protocol: window.location.protocol,
              //   hostname: window.location.hostname
              // })
              
              photoUrl = absoluteUrl
            }
            
            // Double-check: ensure URL is absolute before setting
            let finalAbsoluteUrl = photoUrl.startsWith('http://') || photoUrl.startsWith('https://')
              ? photoUrl
              : `${window.location.origin}${photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`}`
            
            // FINAL FIX: Ensure no spaces remain in the URL (critical - spaces cause 404)
            // Replace ALL spaces and %20 with underscores in the path
            if (finalAbsoluteUrl.includes(' ') || finalAbsoluteUrl.includes('%20')) {
              try {
                const urlObj = new URL(finalAbsoluteUrl)
                // Fix pathname - replace ALL spaces and %20 with underscores
                const fixedPathname = urlObj.pathname.replace(/ /g, '_').replace(/%20/g, '_')
                finalAbsoluteUrl = `${urlObj.origin}${fixedPathname}`
                //console.log('🔧 Final space fix applied:', finalAbsoluteUrl)
              } catch (e) {
                // Manual fix if URL parsing fails
                const originMatch = finalAbsoluteUrl.match(/^(https?:\/\/[^\/]+)/)
                if (originMatch) {
                  const origin = originMatch[1]
                  const path = finalAbsoluteUrl.substring(origin.length)
                  const fixedPath = path.replace(/ /g, '_').replace(/%20/g, '_')
                  finalAbsoluteUrl = origin + fixedPath
                  //console.log('🔧 Manual final space fix:', finalAbsoluteUrl)
                } else {
                  // Last resort: replace all spaces
                  finalAbsoluteUrl = finalAbsoluteUrl.replace(/ /g, '_').replace(/%20/g, '_')
                  //console.log('🔧 Last resort space fix:', finalAbsoluteUrl)
                }
              }
            }
            
            // Ensure proper URL structure (but don't double-encode)
            try {
              const urlObj = new URL(finalAbsoluteUrl)
              // Reconstruct - pathname should already be correct
              finalAbsoluteUrl = `${urlObj.origin}${urlObj.pathname}`
            } catch (e) {
              console.warn('URL parsing failed, but spaces should be fixed:', finalAbsoluteUrl)
            }
            
            // console.log('Setting photo state:', {
            //   receivedFromAPI: data.photograph_url,
            //   afterProcessing: photoUrl,
            //   finalAbsoluteUrl: finalAbsoluteUrl,
            //   windowOrigin: window.location.origin
            // })
            
            // Reset retry count when setting new photo
            imageRetryCountRef.current = 0
            
            // VERIFY: Ensure URL is absolute before setting (one more check)
            const verifiedUrl = finalAbsoluteUrl.startsWith('http://') || finalAbsoluteUrl.startsWith('https://')
              ? finalAbsoluteUrl
              : `${window.location.origin}${finalAbsoluteUrl.startsWith('/') ? finalAbsoluteUrl : `/${finalAbsoluteUrl}`}`
            
            // console.log('✅ Setting photo state with verified URL:', {
            //   before: finalAbsoluteUrl,
            //   after: verifiedUrl,
            //   isAbsolute: verifiedUrl.startsWith('http')
            // })
            
            setExistingPhotoUrl(verifiedUrl)
            setPhotographPreview(verifiedUrl)
            setPhotoCaptured(true)
            //console.log('✅ Photo state set. Final verified URL:', verifiedUrl)
          }
        }
      } catch (error) {
        console.error('Error checking existing photo:', error)
        // Don't block flow if check fails
      } finally {
        setIsCheckingPhoto(false)
      }
    }
    
    checkExistingPhoto()
  }, [sessionId])

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard')
      return
    }

    // Security check: Ensure only candidates can access test pages
    if (user && user.role !== 'candidate') {
      toast.error('Access denied. Only candidates can access test pages.')
      navigate('/dashboard')
      return
    }

    // If no session loaded, redirect back to instructions
    if (!currentSession) {
      navigate(`/test/${sessionId}/instructions`)
      return
    }
    
    // Check browser compatibility first
    checkBrowser()
  }, [sessionId, currentSession, navigate, user])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMediaStream()
      stopAudioAnalysis()
    }
  }, [])

  // Start test when step changes (with duplicate prevention)
  useEffect(() => {
    const testKey = `${currentStep}-${Date.now()}`
    
    // Prevent duplicate test runs for the same step
    if (testRunRef.current === currentStep) {
      //console.log(`Test already running for ${currentStep}, skipping...`)
      return
    }
    
    testRunRef.current = currentStep
    
    if (currentStep === 'camera') {
      testCamera()
    } else if (currentStep === 'microphone') {
      testMicrophone()
    } else if (currentStep === 'network') {
      testNetwork()
    }
  }, [currentStep])

  const checkBrowser = () => {
    setSystemCheck(prev => ({ ...prev, browser: 'testing' }))
    setTimeout(() => {
      const isCompatible = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      setSystemCheck(prev => ({ ...prev, browser: isCompatible }))
      if (!isCompatible) {
        toast.error('Browser not compatible. Please use Chrome, Firefox, or Edge.')
      }
    }, 500)
  }

  const stopMediaStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }

  const stopAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
  }

  const testCamera = async () => {
    setSystemCheck(prev => ({ ...prev, camera: 'testing' }))
    setCameraErrorMessage(null)

    const handleSuccess = (stream: MediaStream) => {
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }

      setSystemCheck(prev => ({ ...prev, camera: true }))
      setCameraErrorMessage(null)
      toast.success('Camera test passed!')
    }

    const handleFailure = (message: string, err?: unknown) => {
      console.error('Camera access error:', err)
      stopMediaStream()
      setSystemCheck(prev => ({ ...prev, camera: false }))
      setCameraErrorMessage(message)
      toast.error(message)
    }

    const getFriendlyErrorMessage = (err: any): string => {
      const errorName = err?.name || err?.code
      switch (errorName) {
        case 'NotAllowedError':
        case 'SecurityError':
          return 'Camera access is blocked. Please allow access in your browser permissions.'
        case 'NotReadableError':
        case 'TrackStartError':
          return 'Your camera is being used by another application. Close other apps and retry.'
        case 'OverconstrainedError':
          return 'The selected camera does not support the requested resolution. Try using a different camera.'
        case 'AbortError':
          return 'Camera initialization was interrupted. Please retry.'
        case 'NotFoundError':
        default:
          return 'Camera access denied. Please ensure a camera is connected and enabled, then retry.'
      }
    }

    const requestCamera = async (constraints?: MediaStreamConstraints) => {
      return navigator.mediaDevices.getUserMedia(
        constraints ?? {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: { ideal: 'user' }
          }
        }
      )
    }

    try {
      const stream = await requestCamera()
      handleSuccess(stream)
      return
    } catch (error: any) {
      // Attempt fallback for missing devices or unsupported constraints
      const isDeviceMissingError =
        error?.name === 'NotFoundError' || /Requested device not found/i.test(error?.message || '')

      if (isDeviceMissingError) {
        // Try with the broadest possible constraint first
        try {
          const broadStream = await requestCamera({ video: true })
          handleSuccess(broadStream)
          return
        } catch (broadError) {
          console.debug('Fallback to broad video constraint failed:', broadError)
        }

        // Try with facingMode constraint (helps mobile/dual camera devices)
        try {
          const facingModeStream = await requestCamera({ video: { facingMode: 'user' } })
          handleSuccess(facingModeStream)
          return
        } catch (facingModeError) {
          console.debug('Fallback to facingMode:user failed:', facingModeError)
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          const videoDevices = devices.filter(device => device.kind === 'videoinput')

          if (videoDevices.length === 0) {
            handleFailure('No camera device detected. Please connect a webcam or enable it in your system settings.', error)
            return
          }

          // Try first available video device explicitly
          const [primaryDevice] = videoDevices
          const deviceIdConstraint = primaryDevice.deviceId
            ? { exact: primaryDevice.deviceId }
            : { ideal: primaryDevice.deviceId }

          const fallbackStream = await requestCamera({
            video: {
              deviceId: deviceIdConstraint,
              facingMode: { ideal: 'user' }
            }
          })

          handleSuccess(fallbackStream)
          return
        } catch (retryError) {
          handleFailure('Unable to access the camera. Ensure it is connected, not used by other apps, and permissions are granted.', retryError)
          return
        }
      }

      const message = getFriendlyErrorMessage(error)
      handleFailure(message, error)
    }
  }

  const testMicrophone = async () => {
    stopMediaStream() // Stop camera first
    setSystemCheck(prev => ({ ...prev, microphone: 'testing' }))
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      
      // Setup audio analysis
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      analyserRef.current = audioContextRef.current.createAnalyser()
      const source = audioContextRef.current.createMediaStreamSource(stream)
      source.connect(analyserRef.current)
      analyserRef.current.fftSize = 256
      
      const bufferLength = analyserRef.current.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)
      
      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b) / bufferLength
          setAudioLevel(Math.min(100, (average / 128) * 100))
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
        }
      }
      
      updateAudioLevel()
      setSystemCheck(prev => ({ ...prev, microphone: true }))
      toast.success('Microphone test passed! Speak to see the audio level.')
    } catch (error) {
      console.error('Microphone access denied:', error)
      setSystemCheck(prev => ({ ...prev, microphone: false }))
      toast.error('Microphone access denied. Please allow microphone access.')
    }
  }

  const testNetwork = async () => {
    stopMediaStream()
    stopAudioAnalysis()
    setSystemCheck(prev => ({ ...prev, internet: 'testing' }))
    setNetworkSpeed({ download: null, upload: null, latency: null })
    setNetworkTestProgress({ phase: 'latency', progress: 0 })
    
    try {
      // Test basic internet connectivity first
      if (!navigator.onLine) {
        throw new Error('No internet connection')
      }

      // Quick latency test
      setNetworkTestProgress({ phase: 'latency', progress: 20 })
      const latencyStart = performance.now()
      
      try {
        // Simple connectivity test with timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout
        
        await fetch('https://www.google.com/favicon.ico?_=' + Date.now(), {
          method: 'HEAD',
          cache: 'no-store',
          mode: 'no-cors',
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)
        const latency = Math.round(performance.now() - latencyStart)
        setNetworkSpeed(prev => ({ ...prev, latency: Math.min(latency, 500) }))
        //console.log('Latency test:', latency, 'ms')
      } catch (latencyError) {
        //console.log('Latency test failed:', latencyError)
        setNetworkSpeed(prev => ({ ...prev, latency: 200 })) // Default reasonable latency
      }
      
      setNetworkTestProgress({ phase: 'latency', progress: 50 })
      
      // Quick download test
      setNetworkTestProgress({ phase: 'download', progress: 60 })
      const downloadStart = performance.now()
      
      try {
        // Use a small, reliable test file
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout
        
        const response = await fetch('https://httpbin.org/bytes/512000?_=' + Date.now(), { // 500KB
          cache: 'no-store',
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)
        const blob = await response.blob()
        const downloadTime = (performance.now() - downloadStart) / 1000
        const downloadSpeed = Math.round((blob.size * 8) / downloadTime / 1000000 * 10) / 10 // Mbps
        
        setNetworkSpeed(prev => ({ ...prev, download: Math.max(0.1, downloadSpeed) }))
        //console.log('Download test:', blob.size, 'bytes in', downloadTime.toFixed(2), 's =', downloadSpeed, 'Mbps')
      } catch (downloadError) {
        //console.log('Download test failed:', downloadError)
        // Estimate based on latency
        const latency = networkSpeed.latency || 200
        const estimatedSpeed = latency < 100 ? 10 : latency < 200 ? 5 : 2
        setNetworkSpeed(prev => ({ ...prev, download: estimatedSpeed }))
      }
      
      setNetworkTestProgress({ phase: 'download', progress: 80 })
      
      // Quick upload test
      setNetworkTestProgress({ phase: 'upload', progress: 90 })
      
      
      try {
        const uploadStart = performance.now()
        const testData = new Array(128 * 1024).fill('A').join('') // 128KB test data
        const testPayload = JSON.stringify({ data: testData, timestamp: Date.now() })
        
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 8000) // 8 second timeout
        
        const response = await fetch('https://httpbin.org/post?_=' + Date.now(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: testPayload,
          cache: 'no-store',
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)
        const uploadTime = (performance.now() - uploadStart) / 1000
        const uploadSpeed = Math.round((new Blob([testPayload]).size * 8) / uploadTime / 1000000 * 10) / 10
        
        setNetworkSpeed(prev => ({ ...prev, upload: Math.max(0.1, uploadSpeed) }))
        //console.log('Upload test:', new Blob([testPayload]).size, 'bytes in', uploadTime.toFixed(2), 's =', uploadSpeed, 'Mbps')
      } catch (uploadError) {
        //console.log('Upload test failed:', uploadError)
        // Estimate upload as 70% of download
        const estimatedUpload = Math.round((networkSpeed.download || 1) * 0.7 * 10) / 10
        setNetworkSpeed(prev => ({ ...prev, upload: estimatedUpload }))
      }
      
      setNetworkTestProgress({ phase: 'complete', progress: 100 })
      
      // Simple connection assessment
      const latency = networkSpeed.latency || 200
      const download = networkSpeed.download || 1
      const upload = networkSpeed.upload || 0.5
      
      // More lenient criteria for better user experience
      const isGoodConnection = latency < 1000 && download >= 0.1 && upload >= 0.1
      setSystemCheck(prev => ({ ...prev, internet: isGoodConnection }))
      
      if (isGoodConnection) {
        toast.success(`Network test completed! ${download}Mbps down, ${upload}Mbps up, ${latency}ms latency`)
      } else {
        toast.error(`Network connection issues detected. Please check your internet connection.`)
        setSystemCheck(prev => ({ ...prev, internet: false }))
      }
      
    } catch (error) {
      //console.error('Network test failed:', error)
      setSystemCheck(prev => ({ ...prev, internet: false }))
      setNetworkSpeed({ download: 0, upload: 0, latency: 9999 })
      setNetworkTestProgress({ phase: null, progress: 0 })
      toast.error('Network test failed. Please check your internet connection.')
    }
  }

  const retryTest = (test: 'camera' | 'microphone' | 'network') => {
    stopMediaStream()
    stopAudioAnalysis()
    testRunRef.current = null // Reset to allow re-running the test
    if (test === 'camera') {
      setCameraErrorMessage(null)
      setPhotoCaptured(false)
      setPhotograph(null)
      setPhotographPreview(null)
    }
    setCurrentStep(test)
  }
  
  const skipToNextStep = (nextStep: 'microphone' | 'network' | 'complete') => {
    setCurrentStep(nextStep)
  }

  // Capture and upload photograph
  const capturePhotograph = async () => {
    if (!videoRef.current || !streamRef.current) {
      toast.error('Camera not available')
      return
    }

    try {
      setIsUploadingPhoto(true)
      
      // Capture photo from video
      let photoBlob = await capturePhotoFromVideo(videoRef.current)
      
      // Compress to max 1.5 MB
      photoBlob = await compressImage(
        new File([photoBlob], 'photo.jpg', { type: 'image/jpeg' }),
        1.5, // Max 1.5 MB
        800, // Max width
        800  // Max height
      )
      
      // Check final size
      const sizeMB = photoBlob.size / (1024 * 1024)
      if (sizeMB > 2) {
        toast.error('Photo is too large. Please try again.')
        setIsUploadingPhoto(false)
        return
      }
      
      setPhotograph(photoBlob)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPhotographPreview(e.target?.result as string)
        setPhotoCaptured(true)
      }
      reader.readAsDataURL(photoBlob)
      
      toast.success(`Photo captured! Size: ${sizeMB.toFixed(2)} MB`)
      
      // Upload photo immediately
      await uploadPhotograph(photoBlob)
      
      // Clear existing photo flag since we just captured a new one
      setExistingPhotoUrl(null)
      
    } catch (error: any) {
      console.error('Error capturing photo:', error)
      toast.error(error.message || 'Failed to capture photo')
    } finally {
      setIsUploadingPhoto(false)
    }
  }

  // Upload photograph to backend
  const uploadPhotograph = async (photoBlob: Blob) => {
    if (!sessionId) {
      toast.error('Session ID not found')
      return
    }

    try {
      const formData = new FormData()
      formData.append('photograph', photoBlob, 'candidate_photo.jpg')
      
      const response = await fetch(`/api/v1/test-sessions/${sessionId}/photograph`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: formData
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to upload photo')
      }
      
      const result = await response.json()
      toast.success('Photograph saved successfully!')
      //console.log('Photo uploaded:', result)
      
    } catch (error: any) {
      console.error('Error uploading photo:', error)
      toast.error(error.message || 'Failed to upload photo')
      throw error
    }
  }

  const handleStartTest = async () => {
    if (!sessionId || !agreed) return

    try {
      setIsStarting(true)
      stopMediaStream()
      stopAudioAnalysis()
      
      // Add timeout wrapper for API call
      const startSessionWithTimeout = async (sessionId: string) => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => {
          controller.abort()
          console.error('Start session timeout after 30 seconds')
        }, 30000) // 30 second timeout
        
        try {
          // Start the session with retry logic
          const maxRetries = 3
          let lastError = null
          
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              //console.log(`Starting session attempt ${attempt}/${maxRetries}...`)
              const result = await dispatch(startSession(sessionId)).unwrap()
              clearTimeout(timeoutId)
              //console.log('Session started successfully:', result)
              return result
            } catch (error: any) {
              lastError = error
              console.error(`Session start attempt ${attempt} failed:`, error)
              
              if (attempt < maxRetries) {
                // Exponential backoff: 1s, 2s, 4s
                const delay = Math.pow(2, attempt - 1) * 1000
                //console.log(`Retrying in ${delay}ms...`)
                await new Promise(resolve => setTimeout(resolve, delay))
              }
            }
          }
          
          clearTimeout(timeoutId)
          throw lastError || new Error('Failed to start session after multiple attempts')
        } catch (error) {
          clearTimeout(timeoutId)
          throw error
        }
      }
      
      // Start session with timeout and retry
      const sessionResult = await startSessionWithTimeout(sessionId)
      
      // Connect WebSocket with timeout (don't fail if WebSocket fails)
      const connectWebSocketWithTimeout = async (sessionId: string) => {
        try {
          const wsPromise = connect(sessionId)
          const timeoutPromise = new Promise<void>((_, reject) => {
            setTimeout(() => reject(new Error('WebSocket connection timeout')), 5000)
          })
          
          await Promise.race([wsPromise, timeoutPromise])
          //console.log('WebSocket connected successfully')
        } catch (error) {
          console.warn('WebSocket connection failed, continuing without real-time updates:', error)
          // Don't throw - continue without WebSocket
        }
      }
      
      await connectWebSocketWithTimeout(sessionId)
      
      // Start timer immediately after session starts (don't wait for WebSocket)
      if (currentSession?.duration_minutes) {
        //console.log('Starting timer with duration:', currentSession.duration_minutes)
        dispatch(startTimer({ 
          remainingTimeSeconds: currentSession.duration_minutes * 60,
          totalDurationMinutes: currentSession.duration_minutes
        }))
      } else if (sessionResult?.duration_minutes) {
        //console.log('Starting timer with duration from session result:', sessionResult.duration_minutes)
        dispatch(startTimer({ 
          remainingTimeSeconds: sessionResult.duration_minutes * 60,
          totalDurationMinutes: sessionResult.duration_minutes
        }))
      }
      
      toast.success('Test started successfully!')
      
      // Mark assessment sequence as started in sessionStorage
      const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
      if (assessmentSequenceStr) {
        sessionStorage.setItem('assessmentSequenceStarted', 'true')
      }
      
      // Add small delay before navigation to ensure state updates
      setTimeout(() => {
        navigate(`/test/${sessionId}`)
      }, 100)
      
    } catch (error: any) {
      console.error('Failed to start test:', error)
      const errorMessage = error?.message || 'Failed to start test. Please try again.'
      
      // More specific error messages
      if (error?.message?.includes('timeout')) {
        toast.error('Connection timeout. Please check your internet connection and try again.')
      } else if (error?.message?.includes('Network')) {
        toast.error('Network error. Please check your internet connection and try again.')
      } else {
        toast.error(errorMessage)
      }
      
      setIsStarting(false)
    }
  }

  const allSystemChecksPass = Object.values(systemCheck).every(check => check === true)
  
  const getStatusIcon = (status: boolean | 'testing') => {
    if (status === 'testing') {
      return <div className="spinner w-5 h-5" />
    }
    return status ? (
      <CheckCircleIcon className="w-5 h-5 text-green-600" />
    ) : (
      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
    )
  }

  if (!currentSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test session...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            System Check & Declaration
          </h1>
          <p className="text-gray-600">
            Complete all system checks before starting the test
          </p>
        </div>

        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="card p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getStatusIcon(systemCheck.camera)}
                <span className="text-sm font-medium">Camera</span>
              </div>
              <div className="flex-1 h-1 mx-4 bg-gray-200 rounded">
                <div className={`h-full bg-blue-600 rounded transition-all ${systemCheck.camera === true ? 'w-full' : 'w-0'}`} />
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(systemCheck.microphone)}
                <span className="text-sm font-medium">Microphone</span>
              </div>
              <div className="flex-1 h-1 mx-4 bg-gray-200 rounded">
                <div className={`h-full bg-blue-600 rounded transition-all ${systemCheck.microphone === true ? 'w-full' : 'w-0'}`} />
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(systemCheck.internet)}
                <span className="text-sm font-medium">Network</span>
              </div>
            </div>
          </div>

          {/* Camera Test */}
          {currentStep === 'camera' && (
            <div className="card p-6 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CameraIcon className="w-6 h-6 mr-2 text-blue-600" />
                Camera Test
              </h3>
              <div className="space-y-4">
                {/* Compact square camera preview */}
                <div className="flex flex-col items-center">
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden border-4 border-gray-300 shadow-md" style={{ width: '200px', height: '200px' }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                    {systemCheck.camera === 'testing' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
                        <div className="text-center">
                          <div className="spinner w-6 h-6 mx-auto mb-2" />
                          <span className="text-white text-xs">Accessing camera...</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 text-center mt-4 max-w-md">
                    {systemCheck.camera === 'testing'
                      ? 'Testing camera access...'
                      : systemCheck.camera
                        ? '✓ Camera is working! Your face should be visible in the frame above.'
                        : cameraErrorMessage || '✗ Camera access denied. Please allow camera access and retry.'}
                  </p>
                </div>
                {/* Photo Capture Section */}
                {systemCheck.camera === true && (
                  <div className="mt-4 space-y-4 border-t pt-4">
                    {isCheckingPhoto ? (
                      <div className="text-center">
                        <div className="spinner w-6 h-6 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Checking for existing photograph...</p>
                      </div>
                    ) : !photoCaptured ? (
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-3">
                          Please capture your photograph before proceeding
                        </p>
                        <button
                          onClick={capturePhotograph}
                          disabled={isUploadingPhoto}
                          className="btn btn-primary flex items-center space-x-2 mx-auto"
                        >
                          {isUploadingPhoto ? (
                            <>
                              <div className="spinner w-4 h-4" />
                              <span>Capturing...</span>
                            </>
                          ) : (
                            <>
                              <CameraIcon className="w-5 h-5" />
                              <span>Capture Photograph</span>
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="text-center space-y-3">
                        <div className="inline-block relative">
                          <img
                            src={(() => {
                              // CRITICAL: Ensure URL has no spaces and is properly encoded
                              const url = photographPreview || existingPhotoUrl || ''
                              if (!url) return ''
                              
                              // If it's a data URL, use as-is
                              if (url.startsWith('data:')) return url
                              
                              // Fix any spaces in the URL (shouldn't happen, but safety check)
                              let fixedUrl = url
                              if (url.includes(' ') || url.includes('%20')) {
                                fixedUrl = url.replace(/ /g, '_').replace(/%20/g, '_')
                                console.warn('⚠️ Found spaces in img src, fixing:', url, '->', fixedUrl)
                              }
                              
                              // Ensure it's absolute if it's not a data URL
                              if (!fixedUrl.startsWith('http://') && !fixedUrl.startsWith('https://') && !fixedUrl.startsWith('data:')) {
                                fixedUrl = `${window.location.origin}${fixedUrl.startsWith('/') ? fixedUrl : `/${fixedUrl}`}`
                              }
                              
                              // CRITICAL: Properly encode the URL path to prevent any decoding issues
                              // Split URL into parts and encode only the path segments (not the whole URL)
                              try {
                                const urlObj = new URL(fixedUrl)
                                // Encode pathname segments properly (but keep slashes)
                                const pathSegments = urlObj.pathname.split('/').map(segment => {
                                  // Don't encode slashes, but encode everything else properly
                                  return segment ? encodeURIComponent(segment) : ''
                                })
                                const encodedPath = pathSegments.join('/')
                                fixedUrl = `${urlObj.origin}${encodedPath}`
                                //console.log('🔧 Final encoded URL for img src:', fixedUrl)
                              } catch (e) {
                                console.warn('URL encoding failed, using as-is:', fixedUrl)
                              }
                              
                              return fixedUrl
                            })()}
                            alt={existingPhotoUrl ? "Existing photo" : "Captured photo"}
                            className="w-32 h-32 object-cover rounded-lg border-2 border-green-500"
                            onLoad={() => {
                              const currentSrc = (document.querySelector('img[alt*="photo"]') as HTMLImageElement)?.src
                              //console.log('✅ Image loaded successfully. URL:', currentSrc || photographPreview || existingPhotoUrl)
                            }}
                            onError={(e) => {
                              // Prevent infinite retry loop - only try once
                              if (imageRetryCountRef.current >= 1) {
                                console.error('Image failed to load after retry. Stopping retries. URL:', (e.target as HTMLImageElement).src)
                                return
                              }
                              
                              imageRetryCountRef.current += 1
                              
                              // Handle image load errors - try to fix URL
                              const img = e.target as HTMLImageElement
                              const currentSrc = img.src
                              //console.error('Failed to load image. Current src:', currentSrc)
                              
                              // Try to fix the URL - ensure it's properly encoded
                              let fixedUrl = currentSrc
                              
                              // Fix relative URLs first (most common issue)
                              if (currentSrc && !currentSrc.startsWith('http://') && !currentSrc.startsWith('https://') && !currentSrc.startsWith('data:')) {
                                // If it's a relative URL, make it absolute
                                fixedUrl = `${window.location.origin}${currentSrc.startsWith('/') ? currentSrc : `/${currentSrc}`}`
                                //console.log('🔧 Converted relative to absolute:', fixedUrl)
                              } else if (currentSrc && (currentSrc.includes(' ') || currentSrc.includes('%20'))) {
                                // Replace spaces with underscores in the path
                                try {
                                  const urlObj = new URL(currentSrc)
                                  // Fix pathname - replace spaces with underscores (correct format)
                                  const fixedPathname = urlObj.pathname.replace(/ /g, '_').replace(/%20/g, '_')
                                  fixedUrl = `${urlObj.origin}${fixedPathname}`
                                  //console.log('🔧 Fixed spaces in URL path:', fixedUrl)
                                } catch (err) {
                                  // If URL parsing fails, try manual fix
                                  const match = currentSrc.match(/\/uploads\/(.+)$/)
                                  if (match) {
                                    const path = match[1]
                                    // Replace spaces with underscores
                                    const fixedPath = path.replace(/ /g, '_').replace(/%20/g, '_')
                                    fixedUrl = `${window.location.origin}/uploads/${fixedPath}`
                                    //console.log('🔧 Manually fixed URL path:', fixedUrl)
                                  } else {
                                    // Try to fix the entire URL
                                    fixedUrl = currentSrc.replace(/ /g, '_').replace(/%20/g, '_')
                                    //console.log('🔧 Fixed entire URL:', fixedUrl)
                                  }
                                }
                              } else if (currentSrc && currentSrc.includes('localhost')) {
                                // Convert localhost to current domain
                                try {
                                  const url = new URL(currentSrc)
                                  fixedUrl = `${window.location.origin}${url.pathname}`
                                  //console.log('🔧 Fixed localhost URL:', fixedUrl)
                                } catch (err) {
                                  console.error('Error fixing image URL:', err)
                                  return
                                }
                              }
                              
                              // Only update if URL changed
                              if (fixedUrl !== currentSrc) {
                                //console.log('Retrying with fixed URL:', fixedUrl)
                                img.src = fixedUrl
                              }
                            }}
                          />
                          <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                            <CheckCircleIcon className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-sm text-green-600 font-medium">
                          {existingPhotoUrl 
                            ? "✓ Photograph already captured (from previous assessment)"
                            : "✓ Photograph captured successfully"}
                        </p>
                        <button
                          onClick={capturePhotograph}
                          disabled={isUploadingPhoto}
                          className="btn btn-outline text-sm"
                        >
                          {existingPhotoUrl ? "Update Photo" : "Retake Photo"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex justify-center space-x-3 pt-2">
                  {systemCheck.camera === false && (
                    <button
                      onClick={() => retryTest('camera')}
                      className="btn btn-outline flex items-center space-x-2"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      <span>Retry Camera</span>
                    </button>
                  )}
                  {systemCheck.camera === true && photoCaptured && (
                    <button
                      onClick={() => skipToNextStep('microphone')}
                      className="btn btn-primary flex items-center space-x-2 px-6 py-3"
                    >
                      <span>Next: Microphone Test →</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Microphone Test */}
          {currentStep === 'microphone' && (
            <div className="card p-6 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <MicrophoneIcon className="w-6 h-6 mr-2 text-blue-600" />
                Microphone Test
              </h3>
              <div className="space-y-4">
                <div className="p-6 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 mb-4 text-center">
                    Speak to test your microphone. The meter below should respond to your voice.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Audio Level</span>
                      <span>{Math.round(audioLevel)}%</span>
                    </div>
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-100 rounded-full ${
                          audioLevel > 60 ? 'bg-green-500' : audioLevel > 30 ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${audioLevel}%` }}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center space-x-2">
                    {systemCheck.microphone === 'testing' && (
                      <>
                        <div className="spinner w-4 h-4" />
                        <span className="text-sm text-gray-600">Testing microphone...</span>
                      </>
                    )}
                    {systemCheck.microphone === true && audioLevel > 10 && (
                      <>
                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-600">Microphone is working!</span>
                      </>
                    )}
                    {systemCheck.microphone === true && audioLevel <= 10 && (
                      <span className="text-sm text-gray-500">Speak to see the audio level</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-center space-x-3">
                  {systemCheck.microphone === false && (
                    <button
                      onClick={() => retryTest('microphone')}
                      className="btn btn-outline flex items-center space-x-2"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      <span>Retry Microphone</span>
                    </button>
                  )}
                  <button
                    onClick={() => retryTest('camera')}
                    className="btn btn-outline"
                  >
                    Back to Camera
                  </button>
                  {systemCheck.microphone === true && (
                    <button
                      onClick={() => skipToNextStep('network')}
                      className="btn btn-primary flex items-center space-x-2 px-6 py-3"
                    >
                      <span>Next: Network Test →</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Network Speed Test */}
          {currentStep === 'network' && (
            <div className="card p-6 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SignalIcon className="w-6 h-6 mr-2 text-blue-600" />
                Network Speed Test
              </h3>
              <div className="space-y-4">
                {/* Live Progress Indicator */}
                {systemCheck.internet === 'testing' && networkTestProgress.phase && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {networkTestProgress.phase === 'latency' && '🔍 Testing Latency...'}
                        {networkTestProgress.phase === 'download' && '⬇️ Testing Download Speed...'}
                        {networkTestProgress.phase === 'upload' && '⬆️ Estimating Upload Speed...'}
                        {networkTestProgress.phase === 'complete' && '✓ Test Complete'}
                      </span>
                      <span className="text-sm font-bold text-blue-600">
                        {networkTestProgress.progress}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${networkTestProgress.progress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Speed Results */}
                <div className="grid grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg text-center transition-all ${
                    networkSpeed.latency !== null ? 'bg-purple-50 scale-105' : 'bg-gray-50'
                  }`}>
                    <div className={`text-2xl font-bold transition-colors ${
                      networkSpeed.latency !== null ? 'text-purple-600' : 'text-gray-400'
                    }`}>
                      {networkSpeed.latency !== null ? `${networkSpeed.latency} ms` : '...'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Latency</div>
                    {networkSpeed.latency !== null && (
                      <div className="mt-1">
                        {networkSpeed.latency < 100 ? (
                          <span className="text-xs text-green-600">● Excellent</span>
                        ) : networkSpeed.latency < 300 ? (
                          <span className="text-xs text-yellow-600">● Good</span>
                        ) : (
                          <span className="text-xs text-red-600">● Poor</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={`p-4 rounded-lg text-center transition-all ${
                    networkSpeed.download !== null ? 'bg-blue-50 scale-105' : 'bg-gray-50'
                  }`}>
                    <div className={`text-2xl font-bold transition-colors ${
                      networkSpeed.download !== null ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {networkSpeed.download !== null ? `${networkSpeed.download} Mbps` : '...'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Download Speed</div>
                    {networkSpeed.download !== null && (
                      <div className="mt-1">
                        {networkSpeed.download >= 10 ? (
                          <span className="text-xs text-green-600">● Excellent</span>
                        ) : networkSpeed.download >= 5 ? (
                          <span className="text-xs text-yellow-600">● Good</span>
                        ) : (
                          <span className="text-xs text-orange-600">● Fair</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={`p-4 rounded-lg text-center transition-all ${
                    networkSpeed.upload !== null ? 'bg-green-50 scale-105' : 'bg-gray-50'
                  }`}>
                    <div className={`text-2xl font-bold transition-colors ${
                      networkSpeed.upload !== null ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {networkSpeed.upload !== null ? `${networkSpeed.upload} Mbps` : '...'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Upload Speed</div>
                    {networkSpeed.upload !== null && (
                      <div className="mt-1">
                        {networkSpeed.upload >= 5 ? (
                          <span className="text-xs text-green-600">● Excellent</span>
                        ) : networkSpeed.upload >= 2 ? (
                          <span className="text-xs text-yellow-600">● Good</span>
                        ) : (
                          <span className="text-xs text-orange-600">● Fair</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {systemCheck.internet === false && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-800 text-center">
                      Poor network connection. Please check your internet and retry.
                    </p>
                  </div>
                )}
                <div className="flex justify-center space-x-3">
                  {systemCheck.internet === false && (
                    <button
                      onClick={() => retryTest('network')}
                      className="btn btn-outline flex items-center space-x-2"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      <span>Retry Network Test</span>
                    </button>
                  )}
                  {systemCheck.internet === true && (
                    <button
                      onClick={() => skipToNextStep('complete')}
                      className="btn btn-primary flex items-center space-x-2 px-6 py-3"
                    >
                      <span>Continue to Declaration →</span>
                    </button>
                  )}
                  <button
                    onClick={() => retryTest('microphone')}
                    className="btn btn-outline"
                  >
                    Back to Microphone
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Browser Check Summary */}
          <div className="card p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ComputerDesktopIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Browser Compatibility</span>
              </div>
              {getStatusIcon(systemCheck.browser)}
            </div>
          </div>

          {/* Declaration & Start */}
          {currentStep === 'complete' && allSystemChecksPass && (
            <>
              <div className="card p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Declaration
                </h3>
                <label htmlFor="agreement-checkbox" className="flex items-start space-x-3 cursor-pointer">
                  <input
                    id="agreement-checkbox"
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    I have read and understood the test instructions. I agree to follow all guidelines and acknowledge that this test is being proctored. I understand that any violation may result in disqualification.
                  </span>
                </label>
              </div>

              <div className="card p-6 shadow-lg">
                <button
                  onClick={handleStartTest}
                  disabled={!agreed || isStarting}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transition-all duration-300 disabled:cursor-not-allowed"
                >
                  {isStarting ? (
                    <>
                      <div className="spinner w-6 h-6" />
                      <span>Starting Test...</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-6 h-6" />
                      <span>Start Test</span>
                    </>
                  )}
                </button>
                
                {!agreed && (
                  <p className="mt-3 text-sm text-gray-500 text-center">
                    Please agree to the terms to start the test
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Assessment Sequence Navigation Warning Modal */}
      {showSequenceWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Navigation Warning</h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Navigation Back from test is not allowed, complete all Assessment in one go. Sure want to leave the test?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={async () => {
                  try {
                    //console.log('[TestSystemCheckPage] Leave Test clicked - submitting all assessments')
                    
                    const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
                    const currentDriveId = sessionStorage.getItem('currentDriveId')
                    const candidateId = user?.id
                    
                    // Submit all assessments in the sequence (mark as completed)
                    if (assessmentSequenceStr && currentDriveId && candidateId) {
                      try {
                        const assessmentSequence = JSON.parse(assessmentSequenceStr)
                        const driveId = parseInt(currentDriveId, 10)
                        
                        // console.log('[TestSystemCheckPage] Submitting all assessments:', {
                        //   driveId,
                        //   totalAssessments: assessmentSequence.length
                        // })
                        
                        // Submit all assessments in the sequence
                        for (const assessment of assessmentSequence) {
                          const assessmentId = assessment.id
                          
                          try {
                            // Mark assessment as submitted (with 0 responses since test wasn't started)
                            await dispatch(submitAssessment({
                              assessmentId,
                              candidateId,
                              driveId
                            }))
                            //console.log(`[TestSystemCheckPage] Marked assessment ${assessmentId} as completed (left test)`)
                          } catch (error) {
                            console.error(`[TestSystemCheckPage] Error submitting assessment ${assessmentId}:`, error)
                            // Continue with other assessments even if one fails
                          }
                        }
                      } catch (error) {
                        console.error('[TestSystemCheckPage] Error processing assessments:', error)
                      }
                    }
                    
                    // Mark drive as left (so Start Test button is disabled)
                    if (currentDriveId) {
                      sessionStorage.setItem(`drive_${currentDriveId}_left`, 'true')
                      //console.log(`[TestSystemCheckPage] Marked drive ${currentDriveId} as left`)
                    }
                    
                    // Clear assessment sequence data
                    sessionStorage.removeItem('currentDriveAssessmentSequence')
                    sessionStorage.removeItem('currentDriveAssessmentIndex')
                    sessionStorage.removeItem('currentDriveId')
                    sessionStorage.removeItem('assessmentSequenceStarted')
                    setShowSequenceWarning(false)
                    
                    toast.success('All assessments have been submitted. You have left the test.')
                    navigate('/dashboard')
                  } catch (error) {
                    console.error('[TestSystemCheckPage] Error leaving test:', error)
                    toast.error('Error leaving test. Please try again.')
                    // Still clear sequence and navigate
                    const currentDriveId = sessionStorage.getItem('currentDriveId')
                    if (currentDriveId) {
                      sessionStorage.setItem(`drive_${currentDriveId}_left`, 'true')
                    }
                    sessionStorage.removeItem('currentDriveAssessmentSequence')
                    sessionStorage.removeItem('currentDriveAssessmentIndex')
                    sessionStorage.removeItem('currentDriveId')
                    sessionStorage.removeItem('assessmentSequenceStarted')
                    setShowSequenceWarning(false)
                    navigate('/dashboard')
                  }
                }}
                className="flex-1 px-4 py-2 border border-red-300 rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors font-medium"
              >
                Leave Test
              </button>
              <button
                onClick={() => {
                  setShowSequenceWarning(false)
                  window.history.pushState(null, '', location.pathname)
                }}
                className="flex-1 px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors font-medium"
              >
                Continue Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestSystemCheckPage

